import React, { useState, useEffect } from 'react';
import {
  Settings,
  Mail,
  Bell,
  Send,
  Save,
  TestTube,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Eye,
  EyeOff,
  Server,
  Shield,
  Calendar,
  Play,
  Timer,
} from 'lucide-react';
import { notificationsApi, schedulerApi } from '../api';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';

const NOTIFICATION_TYPE_LABELS = {
  calibration_due: { label: 'Calibrações Vencendo', icon: '🔧', description: 'Notifica quando instrumentos precisam ser calibrados' },
  nc_deadline: { label: 'Prazos de NC', icon: '⚠️', description: 'Notifica quando Não Conformidades estão próximas do prazo' },
  complaint_overdue: { label: 'Reclamações Atrasadas', icon: '📞', description: 'Notifica quando reclamações estão em atraso' },
  swot_deadline: { label: 'Prazos de Ações SWOT', icon: '🧭', description: 'Notifica o Gestor quando o prazo de um item da Análise SWOT está se aproximando' },
  document_expired: { label: 'Documentos Externos Vencidos', icon: '📄', description: 'Notifica quando um documento externo vence' },
};

const STATUS_LABELS = {
  sent: { label: 'Enviado', color: 'text-green-400', bg: 'bg-green-500/20' },
  failed: { label: 'Falhou', color: 'text-red-400', bg: 'bg-red-500/20' },
  pending: { label: 'Pendente', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  success: { label: 'Sucesso', color: 'text-green-400', bg: 'bg-green-500/20' },
  running: { label: 'Executando', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  partial: { label: 'Parcial', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
};

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Email settings
  const [emailSettings, setEmailSettings] = useState({
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    use_tls: true,
    use_ssl: false,
    sender_email: '',
    sender_name: 'QualySynX SGQ',
    reply_to_email: '',
  });
  const [testEmail, setTestEmail] = useState('');
  
  // Notification configs
  const [notificationConfigs, setNotificationConfigs] = useState([]);
  
  // Notification logs & stats
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total_sent: 0, total_failed: 0, total_pending: 0 });
  
  // Scheduler configs & logs
  const [schedulerConfigs, setSchedulerConfigs] = useState([]);
  const [schedulerLogs, setSchedulerLogs] = useState([]);
  const [schedulerStats, setSchedulerStats] = useState({ total_tasks: 0, enabled_tasks: 0, executions_today: 0 });
  const [runningTask, setRunningTask] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, configsRes, logsRes, statsRes, schConfigsRes, schLogsRes, schStatsRes] = await Promise.all([
        notificationsApi.getEmailSettings(),
        notificationsApi.listConfigs(),
        notificationsApi.listLogs({ limit: 20 }),
        notificationsApi.getStats(),
        schedulerApi.listConfigs(),
        schedulerApi.listLogs({ limit: 10 }),
        schedulerApi.getStats(),
      ]);
      
      if (settingsRes.data) {
        setEmailSettings({
          ...settingsRes.data,
          smtp_password: '',
        });
      }
      
      setNotificationConfigs(configsRes.data || []);
      setLogs(logsRes.data || []);
      setStats(statsRes.data || { total_sent: 0, total_failed: 0, total_pending: 0 });
      setSchedulerConfigs(schConfigsRes.data || []);
      setSchedulerLogs(schLogsRes.data || []);
      setSchedulerStats(schStatsRes.data || { total_tasks: 0, enabled_tasks: 0, executions_today: 0 });
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    if (!emailSettings.smtp_server || !emailSettings.smtp_username || !emailSettings.sender_email) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    try {
      setSaving(true);
      await notificationsApi.saveEmailSettings(emailSettings);
      toast.success('Configurações salvas com sucesso');
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar configurações'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Informe um email para teste');
      return;
    }
    
    try {
      setTesting(true);
      const res = await notificationsApi.testEmail({ recipient_email: testEmail });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao enviar email de teste'));
    } finally {
      setTesting(false);
    }
  };

  const handleUpdateConfig = async (configId, data) => {
    try {
      await notificationsApi.updateConfig(configId, data);
      toast.success('Configuração atualizada');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const handleUpdateSchedulerConfig = async (configId, data) => {
    try {
      await schedulerApi.updateConfig(configId, data);
      toast.success('Agendamento atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar agendamento');
    }
  };

  const handleRunTask = async (taskName) => {
    try {
      setRunningTask(taskName);
      const res = await schedulerApi.runTask(taskName);
      if (res.data.success) {
        toast.success(`Tarefa executada: ${res.data.items_success} enviados, ${res.data.items_failed} falhas`);
      } else {
        toast.error(res.data.message);
      }
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao executar tarefa'));
    } finally {
      setRunningTask(null);
    }
  };

  const handleProcessNotifications = async () => {
    try {
      setLoading(true);
      const res = await notificationsApi.processNotifications();
      toast.success(`Processamento concluído: ${res.data.notifications_sent} enviadas, ${res.data.notifications_failed} falhas`);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao processar notificações'));
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'email', label: 'Configuração de Email', icon: Mail },
    { id: 'scheduler', label: 'Agendamento', icon: Calendar },
    { id: 'notifications', label: 'Tipos de Notificação', icon: Bell },
    { id: 'logs', label: 'Histórico de Envios', icon: Clock },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-green-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="text-green-400" />
            Configurações
          </h1>
          <p className="text-gray-400 mt-1">Configurações de email, agendamento e notificações automáticas</p>
        </div>
        <button
          onClick={handleProcessNotifications}
          className="btn-primary"
          data-testid="process-notifications-btn"
        >
          <Send size={18} />
          Processar Agora
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Emails Enviados</p>
              <p className="text-2xl font-bold text-green-400">{stats.total_sent}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="text-red-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Falhas de Envio</p>
              <p className="text-2xl font-bold text-red-400">{stats.total_failed}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Timer className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Tarefas Ativas</p>
              <p className="text-2xl font-bold text-blue-400">{schedulerStats.enabled_tasks}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Calendar className="text-purple-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Execuções Hoje</p>
              <p className="text-2xl font-bold text-purple-400">{schedulerStats.executions_today}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex gap-4 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-400'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'email' && (
        <EmailSettingsTab
          settings={emailSettings}
          setSettings={setEmailSettings}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          testEmail={testEmail}
          setTestEmail={setTestEmail}
          onSave={handleSaveEmailSettings}
          onTest={handleTestEmail}
          saving={saving}
          testing={testing}
        />
      )}

      {activeTab === 'scheduler' && (
        <SchedulerTab
          configs={schedulerConfigs}
          logs={schedulerLogs}
          stats={schedulerStats}
          onUpdate={handleUpdateSchedulerConfig}
          onRunTask={handleRunTask}
          runningTask={runningTask}
          onRefresh={loadData}
        />
      )}

      {activeTab === 'notifications' && (
        <NotificationConfigsTab
          configs={notificationConfigs}
          onUpdate={handleUpdateConfig}
        />
      )}

      {activeTab === 'logs' && (
        <NotificationLogsTab logs={logs} onRefresh={loadData} />
      )}
    </div>
  );
};

const EmailSettingsTab = ({
  settings,
  setSettings,
  showPassword,
  setShowPassword,
  testEmail,
  setTestEmail,
  onSave,
  onTest,
  saving,
  testing,
}) => (
  <div className="space-y-6">
    <div className="card p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Server className="text-green-400" size={20} />
        Servidor SMTP
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Servidor SMTP *</label>
          <input
            type="text"
            value={settings.smtp_server}
            onChange={(e) => setSettings({ ...settings, smtp_server: e.target.value })}
            placeholder="smtp.gmail.com"
            className="input w-full"
            data-testid="smtp-server-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Porta *</label>
          <input
            type="number"
            value={settings.smtp_port}
            onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
            className="input w-full"
            data-testid="smtp-port-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Usuário SMTP *</label>
          <input
            type="text"
            value={settings.smtp_username}
            onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
            placeholder="seu-email@gmail.com"
            className="input w-full"
            data-testid="smtp-username-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">
            Senha SMTP {settings.id ? '' : '*'}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={settings.smtp_password}
              onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
              placeholder={settings.id ? 'Deixe em branco para manter a senha atual' : '••••••••'}
              className="input w-full pr-10"
              data-testid="smtp-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div className="md:col-span-2 flex gap-4">
          <label className="flex items-center gap-2 text-gray-400">
            <input
              type="checkbox"
              checked={settings.use_tls}
              onChange={(e) => setSettings({ ...settings, use_tls: e.target.checked, use_ssl: false })}
              className="form-checkbox text-green-500"
            />
            Usar TLS (porta 587)
          </label>
          <label className="flex items-center gap-2 text-gray-400">
            <input
              type="checkbox"
              checked={settings.use_ssl}
              onChange={(e) => setSettings({ ...settings, use_ssl: e.target.checked, use_tls: false })}
              className="form-checkbox text-green-500"
            />
            Usar SSL (porta 465)
          </label>
        </div>
      </div>
    </div>

    <div className="card p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Mail className="text-green-400" size={20} />
        Remetente
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Email do Remetente *</label>
          <input
            type="email"
            value={settings.sender_email}
            onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
            placeholder="noreply@empresa.com"
            className="input w-full"
            data-testid="sender-email-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Nome do Remetente</label>
          <input
            type="text"
            value={settings.sender_name}
            onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
            placeholder="QualySynX SGQ"
            className="input w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Email para Resposta (Reply-To)</label>
          <input
            type="email"
            value={settings.reply_to_email || ''}
            onChange={(e) => setSettings({ ...settings, reply_to_email: e.target.value })}
            placeholder="qualidade@empresa.com"
            className="input w-full"
          />
        </div>
      </div>
    </div>

    <div className="flex justify-between items-center">
      <div className="flex items-center gap-4">
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="Email para teste"
          className="input w-64"
          data-testid="test-email-input"
        />
        <button
          onClick={onTest}
          disabled={testing}
          className="btn-secondary"
          data-testid="test-email-btn"
        >
          {testing ? <Loader2 className="animate-spin" size={18} /> : <TestTube size={18} />}
          Enviar Teste
        </button>
      </div>
      <button
        onClick={onSave}
        disabled={saving}
        className="btn-primary"
        data-testid="save-settings-btn"
      >
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
        Salvar Configurações
      </button>
    </div>

    <div className="card p-4 border-l-4 border-blue-500">
      <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
        <Shield size={18} />
        Dicas de Configuração
      </h4>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• <strong>Gmail:</strong> Use smtp.gmail.com, porta 587, TLS. Gere uma "Senha de App" nas configurações de segurança.</li>
        <li>• <strong>Outlook:</strong> Use smtp.office365.com, porta 587, TLS.</li>
        <li>• <strong>Servidor próprio:</strong> Consulte o administrador para as configurações corretas.</li>
      </ul>
    </div>
  </div>
);

const SchedulerTab = ({ configs, logs, stats, onUpdate, onRunTask, runningTask, onRefresh }) => (
  <div className="space-y-6">
    {/* Scheduler Configs */}
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Timer className="text-green-400" size={20} />
        Tarefas Agendadas
      </h3>
      
      {configs.map((config) => (
        <div key={config.id} className="card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-white font-medium text-lg">
                {config.task_name === 'notification_processing' ? '📧 Processamento de Notificações' : config.task_name}
              </h4>
              <p className="text-gray-500 text-sm mt-1">{config.task_description}</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => onRunTask(config.task_name)}
                disabled={runningTask === config.task_name}
                className="btn-secondary text-sm"
                data-testid="run-task-btn"
              >
                {runningTask === config.task_name ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Play size={16} />
                )}
                Executar Agora
              </button>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.is_enabled}
                  onChange={(e) => onUpdate(config.id, { is_enabled: e.target.checked })}
                  className="form-checkbox text-green-500 h-5 w-5"
                  data-testid="scheduler-enabled-checkbox"
                />
                <span className={config.is_enabled ? 'text-green-400 font-medium' : 'text-gray-500'}>
                  {config.is_enabled ? 'Ativo' : 'Inativo'}
                </span>
              </label>
            </div>
          </div>
          
          {config.is_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-700">
              {/* Time selector */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Horário de Execução</label>
                <input
                  type="time"
                  value={config.execution_time ? config.execution_time.substring(0, 5) : '08:00'}
                  onChange={(e) => onUpdate(config.id, { execution_time: e.target.value + ':00' })}
                  className="input w-32"
                  data-testid="scheduler-time-input"
                />
              </div>
              
              {/* Days of week selector */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Dias da Semana</label>
                <div className="flex gap-2">
                  {DAY_NAMES.map((day, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        const days = config.days_of_week || [];
                        const newDays = days.includes(index)
                          ? days.filter(d => d !== index)
                          : [...days, index].sort();
                        onUpdate(config.id, { days_of_week: newDays });
                      }}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        (config.days_of_week || []).includes(index)
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Next execution */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-400">
                    <Clock size={16} className="inline mr-1" />
                    Próxima execução:
                  </span>
                  <span className="text-white font-medium">
                    {config.next_execution
                      ? new Date(config.next_execution).toLocaleString('pt-BR')
                      : 'Não agendada'}
                  </span>
                  
                  {config.last_execution && (
                    <>
                      <span className="text-gray-600">|</span>
                      <span className="text-gray-400">
                        Última execução:
                      </span>
                      <span className={config.last_execution_status === 'success' ? 'text-green-400' : 'text-yellow-400'}>
                        {new Date(config.last_execution).toLocaleString('pt-BR')}
                        {config.last_execution_message && ` - ${config.last_execution_message}`}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Stats */}
          <div className="flex gap-6 mt-4 pt-4 border-t border-gray-700 text-sm">
            <span className="text-gray-400">
              Total: <span className="text-white font-medium">{config.total_executions || 0}</span>
            </span>
            <span className="text-gray-400">
              Sucesso: <span className="text-green-400 font-medium">{config.successful_executions || 0}</span>
            </span>
            <span className="text-gray-400">
              Falhas: <span className="text-red-400 font-medium">{config.failed_executions || 0}</span>
            </span>
          </div>
        </div>
      ))}
    </div>
    
    {/* Scheduler Logs */}
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Histórico de Execuções</h3>
        <button onClick={onRefresh} className="btn-secondary text-sm">
          <RefreshCw size={16} />
          Atualizar
        </button>
      </div>
      
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Início</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tarefa</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Duração</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Processados</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Sucesso</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Falhas</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Nenhuma execução registrada</td>
              </tr>
            ) : (
              logs.map((log) => {
                const statusInfo = STATUS_LABELS[log.status] || STATUS_LABELS.pending;
                return (
                  <tr key={log.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {new Date(log.started_at).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-3 text-sm text-white">
                      {log.task_name === 'notification_processing' ? 'Notificações' : log.task_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {log.duration_seconds ? `${log.duration_seconds}s` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-white">{log.items_processed}</td>
                    <td className="px-4 py-3 text-sm text-green-400">{log.items_success}</td>
                    <td className="px-4 py-3 text-sm text-red-400">{log.items_failed}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusInfo.bg} ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const NotificationConfigsTab = ({ configs, onUpdate }) => (
  <div className="space-y-4">
    {configs.map((config) => {
      const typeInfo = NOTIFICATION_TYPE_LABELS[config.notification_type] || { label: config.notification_type, icon: '📧' };
      
      return (
        <div key={config.id} className="card p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <span className="text-2xl">{typeInfo.icon}</span>
              <div>
                <h4 className="text-white font-medium">{typeInfo.label}</h4>
                <p className="text-gray-500 text-sm">{typeInfo.description}</p>
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.is_enabled}
                onChange={(e) => onUpdate(config.id, { is_enabled: e.target.checked })}
                className="form-checkbox text-green-500 h-5 w-5"
              />
              <span className={config.is_enabled ? 'text-green-400' : 'text-gray-500'}>
                {config.is_enabled ? 'Ativo' : 'Inativo'}
              </span>
            </label>
          </div>
          
          {config.is_enabled && (
            <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Dias antes para notificar</label>
                <input
                  type="text"
                  value={(config.days_before || []).join(', ')}
                  onChange={(e) => {
                    const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d));
                    onUpdate(config.id, { days_before: days });
                  }}
                  placeholder="30, 15, 7, 1"
                  className="input w-full text-sm"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-gray-400 text-sm">
                  <input
                    type="checkbox"
                    checked={config.send_to_responsible}
                    onChange={(e) => onUpdate(config.id, { send_to_responsible: e.target.checked })}
                    className="form-checkbox text-green-500"
                  />
                  Responsável
                </label>
                <label className="flex items-center gap-2 text-gray-400 text-sm">
                  <input
                    type="checkbox"
                    checked={config.send_to_admin}
                    onChange={(e) => onUpdate(config.id, { send_to_admin: e.target.checked })}
                    className="form-checkbox text-green-500"
                  />
                  Administradores
                </label>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Emails adicionais</label>
                <input
                  type="text"
                  value={(config.additional_emails || []).join(', ')}
                  onChange={(e) => {
                    const emails = e.target.value.split(',').map(e => e.trim()).filter(e => e);
                    onUpdate(config.id, { additional_emails: emails });
                  }}
                  placeholder="email1@emp.com, email2@emp.com"
                  className="input w-full text-sm"
                />
              </div>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const NotificationLogsTab = ({ logs, onRefresh }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <h3 className="text-white font-medium">Últimos Envios</h3>
      <button onClick={onRefresh} className="btn-secondary text-sm">
        <RefreshCw size={16} />
        Atualizar
      </button>
    </div>
    
    <div className="card overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-800">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Destinatário</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Assunto</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Referência</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {logs.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nenhum envio registrado</td>
            </tr>
          ) : (
            logs.map((log) => {
              const statusInfo = STATUS_LABELS[log.status] || STATUS_LABELS.pending;
              const typeInfo = NOTIFICATION_TYPE_LABELS[log.notification_type] || { label: log.notification_type, icon: '📧' };
              
              return (
                <tr key={log.id} className="hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {new Date(log.created_at).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="flex items-center gap-1">
                      {typeInfo.icon} {typeInfo.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-white">
                    {log.recipient_name || log.recipient_email}
                    {log.recipient_name && (
                      <span className="text-gray-500 text-xs block">{log.recipient_email}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400 max-w-xs truncate">{log.subject}</td>
                  <td className="px-4 py-3 text-sm text-gray-400 font-mono">{log.reference_code || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${statusInfo.bg} ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    {log.error_message && (
                      <span className="block text-red-400 text-xs mt-1 max-w-xs truncate" title={log.error_message}>
                        {log.error_message}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default SettingsPage;
